import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { updateConfig } from "../../../../store/slices/Delivery/invoicesSlice";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import {
  useGetCashReceiptListDataQuery,
  useReprintCashReceiptMutation,
  useEditCashReceiptMutation,
  useCancelCashReceiptMutation,
  useUpdateCashReceiptTableHeadDataMutation,
} from "../../../../store/queries/Delivery";
import { exportCashReceipt } from "../api";
import useReceiptPrint from "../useReceiptPrint";
import useTabFilters from "../useTabFilters";

const useCompleted = () => {
  const paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const dispatch = useDispatch();
  const { receiptRef, triggerReceiptPrint } = useReceiptPrint();
  const reduxState = useSelector((state) => state.deliveryInvoices);
  const { showEditModal } = useSelector((state) => state.global);
  const queryState = reduxState?.completedTab;

  const {
    date,
    formattedDate,
    handlePageSize,
    handlePagination,
    handleSort,
    handleSearch,
    handleDateChange,
  } = useTabFilters("completedTab");

  const [reprintCashReceipt, { isLoading: isReprinting }] =
    useReprintCashReceiptMutation();
  const [editCashReceipt, { isLoading: isFetchingEdit }] =
    useEditCashReceiptMutation();
  const [cancelCashReceipt, { isLoading: isCancelling }] =
    useCancelCashReceiptMutation();
  const [updateCashReceiptFields] = useUpdateCashReceiptTableHeadDataMutation();
  const [isExporting, setIsExporting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  const {
    data: currentData = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetCashReceiptListDataQuery({
    per_page: queryState?.currentPageSize,
    page: queryState?.currentPage,
    sort_by: queryState?.sortBy,
    sort_order: queryState?.sortOrder,
    search: queryState?.search,
    from_date: formattedDate[0],
    to_date: formattedDate[1],
  });

  const tableFields = currentData?.data?.fields;

  // Cancelled receipts can no longer be edited, cancelled or reprinted, so
  // mark them as disabled to hide their row checkbox (Table's `disable` key).
  const tableData = (currentData?.data?.data || []).map((row) =>
    String(row?.status).toLowerCase() === "cancelled"
      ? { ...row, disable: true }
      : row,
  );

  // Show edit / cancel actions only when the API allows it (default to showing
  // when no flag is present).
  const editPermission = currentData?.data?.update_permission ?? true;
  const cancelPermission = currentData?.data?.cancel_permission ?? true;

  const getRow = (field, data) => {
    const renderers = {
      common: (f, d) => <p className="pro-mb-0">{d[f]}</p>,
    };
    return (renderers[field] ?? renderers.common)(field, data);
  };

  const clearSelection = () => {
    dispatch(
      updateConfig((state) => {
        state.completedTab.clear = true;
      }),
    );
    Promise.resolve().then(() => {
      dispatch(
        updateConfig((state) => {
          state.completedTab.clear = false;
        }),
      );
    });
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };

  const handleExport = () => {
    const payload = {
      sort_by: queryState?.sortBy,
      sort_order: queryState?.sortOrder,
      search: queryState?.search,
      from_date: formattedDate[0],
      to_date: formattedDate[1],
    };
    setIsExporting(true);
    exportCashReceipt(payload)
      .then((response) => {
        setIsExporting(false);
        if (response?.status === 200) {
          const dataType = response?.data?.type;
          const downloadLink = document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(
            new Blob([response?.data], { type: dataType }),
          );
          downloadLink.setAttribute(
            "download",
            `Cash_Receipts_${moment(Date.now()).format(
              "DD_MM_YYYY_HH_mm_ss",
            )}.xlsx`,
          );
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          toast.success("Cash receipts exported successfully");
        } else {
          toast.error("Failed to export cash receipts");
        }
      })
      .catch(() => {
        setIsExporting(false);
        toast.error("Failed to export cash receipts");
      });
  };

  const handleReprint = (selectedIds) => {
    const id = selectedIds?.[0];
    if (!id) {
      toast.warn("Select a cash receipt to print");
      return;
    }
    reprintCashReceipt({ cash_receipt_id: id })
      .unwrap()
      .then((response) => {
        if (response?.status_code === 200 || response?.success) {
          dispatch(
            updateConfig((state) => {
              state.receiptPrintData = response?.data ?? response;
            }),
          );
          triggerReceiptPrint();
        } else {
          toast.error(response?.message || "Failed to print receipt");
        }
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to print receipt");
      });
  };

  // Edit — fetch the receipt and open the matching slip modal based on type.
  // type 3 => supplier payment, type 1/2 => invoice cash receipt.
  const handleEdit = (selectedIds) => {
    const id = selectedIds?.[0];
    if (!id) {
      toast.warn("Select a cash receipt to edit");
      return;
    }
    // The completed list row carries the `type` key: 3 => supplier payment,
    // 1/2 => invoice cash receipt. Use it to pick the matching slip modal.
    const row = (currentData?.data?.data || []).find((r) => r?._id === id);
    const isSupplier = Number(row?.type) === 3;
    editCashReceipt({ cash_receipt_id: id })
      .unwrap()
      .then((response) => {
        if (response?.status_code === 200 || response?.success) {
          const data = response?.data ?? response;
          dispatch(
            updateConfig((state) => {
              state.editData = {
                ...data,
                cash_receipt_id: id,
                type: row?.type,
              };
              state.showEditSupplierModal = isSupplier;
              state.showEditReceiptModal = !isSupplier;
            }),
          );
        } else {
          toast.error(response?.message || "Failed to load cash receipt");
        }
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to load cash receipt");
      });
  };

  const handleCloseEditModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showEditReceiptModal = false;
        state.showEditSupplierModal = false;
        state.editData = null;
      }),
    );
    clearSelection();
  };

  // Cancel — open a small modal to capture an optional reason, then confirm.
  const handleCancelClick = (selectedIds) => {
    const id = selectedIds?.[0];
    if (!id) {
      toast.warn("Select a cash receipt to cancel");
      return;
    }
    setCancellationReason("");
    dispatch(
      updateConfig((state) => {
        state.cancelTargetId = id;
        state.showCancelModal = true;
      }),
    );
  };

  const handleCloseCancelModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCancelModal = false;
        state.cancelTargetId = null;
      }),
    );
    setCancellationReason("");
  };

  const handleConfirmCancel = () => {
    const id = reduxState?.cancelTargetId;
    if (!id) return;
    cancelCashReceipt({
      cash_receipt_id: id,
      cancellation_reason: cancellationReason || null,
    })
      .unwrap()
      .then((response) => {
        if (response?.status_code === 200 || response?.success) {
          toast.success(response?.message || "Cash receipt cancelled");
          handleCloseCancelModal();
          clearSelection();
        } else {
          toast.error(response?.message || "Failed to cancel cash receipt");
        }
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to cancel cash receipt");
      });
  };

  return {
    paginationOptions,
    queryState,
    reduxState,
    currentData,
    isFetching,
    isLoading,
    isReprinting,
    isFetchingEdit,
    isCancelling,
    isExporting,
    showEditModal,
    editPermission,
    cancelPermission,
    tableFields,
    tableData,
    receiptRef,
    date,
    cancellationReason,
    setCancellationReason,
    refetch,
    getRow,
    handleDateChange,
    handleSearch,
    handleSort,
    handlePageSize,
    handlePagination,
    handleReprint,
    handleEdit,
    handleCloseEditModal,
    handleCancelClick,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleExport,
    handleEditColumnsClick,
    updateCashReceiptFields,
    triggerReceiptPrint,
  };
};

export default useCompleted;
